import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import Metrics from '@spotahome/soyuz-tracking';

import useCookieConsent from '../../../hooks/useCookieConsent';

import YouTubeIframeLoader from './YouTubeIframeLoader';
import YoutubePlaceholder from './YoutubePlaceholder';

import style from './YoutubeVideo.module.scss';

const YOUTUBE_PLAYING_STATE = 1;
const YOUTUBE_PAUSED_STATE = 2;

const YoutubeVideo = ({
  video,
  title,
  thumbnail = {},
  autoplay = false,
  watermark = null,
  listingId,
  playing = false
}) => {
  const [requestedLoad, setRequestedLoad] = useState(autoplay);
  const [showNoCookiesIframe, setShowNoCookiesIframe] = useState(false);
  const playerRef = useRef(null);
  const currentVideoId = useRef();
  const cookieConsent = useCookieConsent();

  const loadVideo = useCallback(async videoId => {
    if (
      !cookieConsent.isAdvertisementCookiesAccepted() ||
      !cookieConsent.isFunctionalCookiesAccepted()
    ) {
      setShowNoCookiesIframe(true);
      return;
    }

    try {
      YouTubeIframeLoader.load(YT => {
        playerRef.current = new YT.Player(`yt-${videoId}`, {
          videoId,
          height: '100%',
          width: '100%',
          playerVars: {
            rel: 0,
            host: 'https://www.youtube-nocookie.com',
            origin: window.location.origin
          },
          events: {
            onReady: params => {
              playerRef.current = params.target;
              params.target.playVideo();
            },
            onStateChange: () => {
              if (playerRef.current.setOption) {
                playerRef.current.setOption('captions', 'track', {
                  languageCode: 'en'
                });
              }
            },
            onError: event => {
              console.error(`Error: ${event.data}`);
            }
          }
        });
      });
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    const prevVideoId = currentVideoId.current;

    if (prevVideoId !== video.videoId) {
      if (!autoplay) {
        setRequestedLoad(false);
      } else if (requestedLoad) {
        loadVideo(video.videoId);
      }
    }

    currentVideoId.current = video.videoId;
  }, [requestedLoad, video.videoId, loadVideo]);

  useEffect(() => {
    if (
      !playing &&
      playerRef.current?.getPlayerState() === YOUTUBE_PLAYING_STATE
    ) {
      playerRef.current?.pauseVideo();
    }

    if (
      playing &&
      playerRef.current?.getPlayerState() === YOUTUBE_PAUSED_STATE
    ) {
      playerRef.current?.playVideo();
    }
  }, [playing]);

  const requestVideoLoad = () => {
    setRequestedLoad(true);
    loadVideo(video.videoId);
    Metrics.actions.sendActionEvent(Metrics.actions.WATCH_VERIFICATION_VIDEO, {
      listingId
    });
  };

  return (
    <div className={style['yt-video']}>
      {!requestedLoad && (
        <YoutubePlaceholder
          name={title}
          id={video.videoId}
          url={video.embedUrl}
          handleClick={requestVideoLoad}
          thumbnail={thumbnail}
        />
      )}
      {showNoCookiesIframe && (
        <iframe
          title="yt-video-iframe-nocookie"
          width="100%"
          height="100%"
          src={`https://www.youtube-nocookie.com/embed/${video.videoId}?autoplay=1`}
          allow="autoplay; encrypted-media"
          allowFullScreen
        />
      )}
      <div
        className={style['yt-video__container']}
        key={`video-${video.embedUrl}`}
      >
        <div id={`yt-${video.videoId}`} />
        {watermark}
      </div>
    </div>
  );
};

YoutubeVideo.propTypes = {
  video: PropTypes.shape({
    embedUrl: PropTypes.string,
    videoId: PropTypes.string,
    description: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    uploadDate: PropTypes.string
  }).isRequired,
  title: PropTypes.string.isRequired,
  thumbnail: YoutubePlaceholder.propTypes.thumbnail,
  autoplay: PropTypes.bool,
  watermark: PropTypes.node,
  listingId: PropTypes.string.isRequired,
  playing: PropTypes.bool
};

export default YoutubeVideo;
